/**
 * WLED Effects Types
 * 
 * Types for managing WLED effects, palettes, and gradients
 */

/**
 * WLED effect definition
 */
export interface WledEffect {
  id: number
  name: string
}

/**
 * WLED palette definition
 */
export interface WledPalette {
  id: number
  name: string
}

/**
 * A color stop in a gradient
 * Position is 0-1 (0% to 100%)
 */
export interface GradientStop {
  id: string           // Unique ID for Vue key
  position: number     // 0-1 (position along gradient)
  color: {
    hue: number        // 0-65535 (Hue bridge scale)
    saturation: number // 0-254 (Hue bridge scale)
    brightness: number // 0-254 (Hue bridge scale)
  }
}

/**
 * Gradient definition with multiple color stops
 */
export interface Gradient {
  stops: GradientStop[]
}

/**
 * Complete effect state for a WLED light/segment
 */
export interface WledEffectState {
  effectId: number        // WLED effect ID (0 = solid)
  effectName: string      // Effect name for display
  speed: number           // 0-255
  intensity: number       // 0-255
  paletteId?: number      // Optional WLED palette ID
  gradient?: Gradient     // Custom gradient (if not using palette)
}

/**
 * WLED effect command sent to the device
 */
export interface WledEffectCommand {
  seg: Array<{
    id: number
    fx: number           // Effect ID
    sx?: number          // Speed (0-255)
    ix?: number          // Intensity (0-255)
    pal?: number         // Palette ID
    col?: number[][]     // Colors [primary, secondary, tertiary]
  }>
  transition?: number    // Transition time in 100ms units
}

/**
 * Default gradient with 2 stops (simple gradient)
 */
export const DEFAULT_GRADIENT: Gradient = {
  stops: [
    {
      id: 'stop-1',
      position: 0,
      color: { hue: 0, saturation: 254, brightness: 254 } // Red
    },
    {
      id: 'stop-2',
      position: 1,
      color: { hue: 43690, saturation: 254, brightness: 254 } // Blue
    }
  ]
}

/**
 * Create default effect settings for a new WLED light
 * Deep copies the gradient to avoid shared references
 */
export function createDefaultWledEffectSettings(): WledEffectState {
  return {
    effectId: 0,
    effectName: 'Solid',
    speed: 128,
    intensity: 128,
    paletteId: undefined,
    gradient: {
      stops: DEFAULT_GRADIENT.stops.map(s => ({
        id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        position: s.position,
        color: { ...s.color }
      }))
    }
  }
}

/**
 * List of common WLED effects
 * Full list has 100+ effects, these are the most commonly used
 */
export const COMMON_EFFECTS: WledEffect[] = [
  { id: 0, name: 'Solid' },
  { id: 1, name: 'Blink' },
  { id: 2, name: 'Breathe' },
  { id: 3, name: 'Wipe' },
  { id: 4, name: 'Wipe Random' },
  { id: 5, name: 'Random Colors' },
  { id: 6, name: 'Sweep' },
  { id: 7, name: 'Dynamic' },
  { id: 8, name: 'Colorloop' },
  { id: 9, name: 'Rainbow' },
  { id: 10, name: 'Scan' },
  { id: 11, name: 'Scan Dual' },
  { id: 12, name: 'Fade' },
  { id: 13, name: 'Theater' },
  { id: 14, name: 'Theater Rainbow' },
  { id: 15, name: 'Running' },
  { id: 16, name: 'Saw' },
  { id: 17, name: 'Twinkle' },
  { id: 18, name: 'Dissolve' },
  { id: 19, name: 'Dissolve Rnd' },
  { id: 20, name: 'Sparkle' },
  { id: 21, name: 'Sparkle Dark' },
  { id: 22, name: 'Sparkle+' },
  { id: 23, name: 'Strobe' },
  { id: 24, name: 'Strobe Rainbow' },
  { id: 25, name: 'Strobe Mega' },
  { id: 26, name: 'Blink Rainbow' },
  { id: 27, name: 'Android' },
  { id: 28, name: 'Chase' },
  { id: 29, name: 'Chase Random' },
  { id: 30, name: 'Chase Rainbow' },
  { id: 31, name: 'Chase Flash' },
  { id: 32, name: 'Chase Flash Rnd' },
  { id: 33, name: 'Rainbow Runner' },
  { id: 34, name: 'Colorful' },
  { id: 35, name: 'Traffic Light' },
  { id: 36, name: 'Sweep Random' },
  { id: 37, name: 'Chase 2' },
  { id: 38, name: 'Aurora' },
  { id: 39, name: 'Stream' },
  { id: 40, name: 'Scanner' },
  { id: 41, name: 'Lighthouse' },
  { id: 42, name: 'Fireworks' },
  { id: 43, name: 'Rain' },
  { id: 44, name: 'Tetrix' },
  { id: 45, name: 'Fire Flicker' },
  { id: 46, name: 'Gradient' },
  { id: 47, name: 'Loading' },
  { id: 48, name: 'Police' },
  { id: 49, name: 'Fairy' },
  { id: 50, name: 'Two Dots' },
  { id: 51, name: 'Fairytwinkle' },
  { id: 52, name: 'Running Dual' },
  // Add more as needed...
]

/**
 * Common WLED palettes
 */
export const COMMON_PALETTES: WledPalette[] = [
  { id: 0, name: 'Default' },
  { id: 1, name: 'Random Cycle' },
  { id: 2, name: 'Color 1' },
  { id: 3, name: 'Colors 1&2' },
  { id: 4, name: 'Color Gradient' },
  { id: 5, name: 'Colors Only' },
  { id: 6, name: 'Party' },
  { id: 7, name: 'Cloud' },
  { id: 8, name: 'Lava' },
  { id: 9, name: 'Ocean' },
  { id: 10, name: 'Forest' },
  { id: 11, name: 'Rainbow' },
  { id: 12, name: 'Rainbow Bands' },
  { id: 13, name: 'Sunset' },
  { id: 14, name: 'Rivendell' },
  { id: 15, name: 'Breeze' },
  { id: 16, name: 'Red & Blue' },
  { id: 17, name: 'Yellowout' },
  { id: 18, name: 'Analogous' },
  { id: 19, name: 'Splash' },
  { id: 20, name: 'Pastel' },
  // Add more as needed...
]

/**
 * Convert Hue-scale HSB to CSS hsl() string
 */
export function hueHsbToCss(hue: number, saturation: number, brightness: number): string {
  // Hue: 0-65535 -> 0-360
  const h = Math.round((hue / 65535) * 360)
  // Saturation: 0-254 -> 0-100%
  const s = Math.round((saturation / 254) * 100)
  // Brightness: 0-254 -> 0-100% (as lightness, roughly 50% is full brightness)
  const l = Math.round((brightness / 254) * 50)
  return `hsl(${h}, ${s}%, ${l}%)`
}

/**
 * Convert Hue-scale HSB to RGB array for WLED
 */
export function hueHsbToRgb(hue: number, saturation: number, brightness: number): [number, number, number] {
  // Hue: 0-65535 -> 0-360
  const h = (hue / 65535) * 360
  // Saturation: 0-254 -> 0-1
  const s = saturation / 254
  // Brightness: 0-254 -> 0-1
  const v = brightness / 254

  // HSV to RGB conversion
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c

  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x; b = 0 }
  else if (h < 120) { r = x; g = c; b = 0 }
  else if (h < 180) { r = 0; g = c; b = x }
  else if (h < 240) { r = 0; g = x; b = c }
  else if (h < 300) { r = x; g = 0; b = c }
  else { r = c; g = 0; b = x }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ]
}

/**
 * Generate a unique ID for a gradient stop
 */
export function generateStopId(): string {
  return `stop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}
